import type {Locale} from '@commercial-website/data-layer/locale'
import {locales as defaultLocales} from '@commercial-website/data-layer/locale'
import {LocaleSwitcher as UILocaleSwitcher} from '@commercial-website/ui'
import Cookies from 'js-cookie'
import React from 'react'

import {TrackedLocalizedLink} from '@/components/localized-link'
import {useLocale} from '@/providers/locale'
import {countryFlags} from '@/utils/country-flags'

import {cookiesMap} from '@helios/website/src/config/cookies'
import {usePageInfo} from '@helios/website/src/providers/page-info'

interface LocaleSwitcherProps {
    locales?: Locale[]
}

type UILocaleSwitcherProps = React.ComponentProps<typeof UILocaleSwitcher>
type LocaleRender = NonNullable<UILocaleSwitcherProps['locales'][number]['render']>

// A tracked link that lets the Select drive navigation via `onLocaleChange`.
const renderLocaleLink =
    (locale: Locale): LocaleRender =>
    ({children, onClick, ...props}) => (
        <TrackedLocalizedLink
            trackingData={{
                linkText: locale.countryName,
                linkType: 'element',
                elementLocation: 'locale switcher',
            }}
            rawPath
            to={`/${locale.urlCode}`}
            {...props}
            onClick={(e) => {
                e.preventDefault()
                onClick?.(e)
            }}
        >
            {children}
        </TrackedLocalizedLink>
    )

const mapLocales = (locales: Locale[]): UILocaleSwitcherProps['locales'] =>
    locales.map((locale) => ({
        code: locale.code,
        countryName: locale.countryName,
        languageName: locale.languageName,
        flag: countryFlags[locale.country],
        render: renderLocaleLink(locale),
    }))

const LocaleSwitcher = ({locales: customLocales}: LocaleSwitcherProps) => {
    const currentLocale = useLocale()
    const {pageContext} = usePageInfo()
    const locales = customLocales ?? defaultLocales

    /*
     * Using window.location.assign instead of navigate to force a full page reload.
     * This ensures that the correct experiment cookies are evaluated for the new locale,
     * preventing the user from remaining stuck in a variant specific to their previous locale.
     */
    const handleLocaleSwitch = (localeCode: string) => {
        const search = window.location.search
        const targetLocale = locales.find((locale) => locale.code === localeCode) ?? locales[0]
        const destination = pageContext.paths?.[targetLocale.code] ?? `/${targetLocale.urlCode}`

        Cookies.set(cookiesMap.defaultCountry, targetLocale.urlCode)

        window.location.assign(`${destination}${search}`)
    }

    return (
        <UILocaleSwitcher
            locales={mapLocales(locales)}
            currentLocaleCode={currentLocale.code}
            onLocaleChange={handleLocaleSwitch}
        />
    )
}

export default LocaleSwitcher
